import { create } from 'zustand'
import { api, registerAuthCallbacks } from '../services/api'
import { authService } from '../services/auth'
import { useTenantSelectionStore } from './tenantSelectionStore'
import { queryClient } from '../lib/queryClient'
import type { Usuario, LoginCredentials } from '../types'

interface AuthState {
  user:         Usuario | null
  /** Access token JWT (expira 15 min), conservado únicamente en memoria. */
  accessToken:  string | null
  isHydrated:   boolean

  // ─── Acciones públicas ─────────────────────────────────────────────────────
  login:        (credentials: LoginCredentials) => Promise<Usuario>
  logout:       () => Promise<void>
  setUser:      (user: Usuario) => void
  /** Actualiza el access token después de un refresh exitoso (llamado por api.ts). */
  setAccessToken: (accessToken: string) => void
  hydrateSession: () => Promise<void>
  /** Limpia sesión local sin llamar al servidor (usado por el interceptor cuando el refresh falla). */
  clearSession: () => void

  // ─── Selectores ────────────────────────────────────────────────────────────
  isAuthenticated: () => boolean
  isConformity:    () => boolean
  canWrite:        () => boolean
}

export const useAuthStore = create<AuthState>()(
    (set, get) => ({
      user:         null,
      accessToken:  null,
      isHydrated:   false,

      login: async (credentials) => {
        const data = await authService.login(credentials)
        // Las queries anteriores pueden contener datos de otro tenant en el mismo navegador.
        queryClient.clear()
        // Inyectar access token en el header global de axios para todas las peticiones siguientes
        api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
        set({ user: data.user, accessToken: data.accessToken, isHydrated: true })
        return data.user
      },

      logout: async () => {
        try {
          await authService.logout()
        } catch {
          // Continuar aunque el servidor falle — limpiar sesión local de todas formas
        }
        api.defaults.headers.common['Authorization'] = ''
        queryClient.clear()
        set({ user: null, accessToken: null, isHydrated: true })
        useTenantSelectionStore.getState().setSelectedTenantId(null)
      },

      setUser: (user) => set({ user }),

      setAccessToken: (accessToken) => {
        api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
        set({ accessToken })
      },

      hydrateSession: async () => {
        try {
          const { accessToken } = await authService.refresh()
          api.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`
          const user = await authService.me()
          set({ user, accessToken, isHydrated: true })
        } catch {
          api.defaults.headers.common['Authorization'] = ''
          queryClient.clear()
          set({ user: null, accessToken: null, isHydrated: true })
        }
      },

      clearSession: () => {
        api.defaults.headers.common['Authorization'] = ''
        queryClient.clear()
        set({ user: null, accessToken: null, isHydrated: true })
        useTenantSelectionStore.getState().setSelectedTenantId(null)
        window.location.replace('/login')
      },

      isAuthenticated: () => !!get().accessToken && !!get().user,

      isConformity: () => get().user?.rol === 'conformity',
      canWrite:     () => get().user?.nivel_acceso === 'ESCRITURA',
    }),
)

// ─── Registrar callbacks en api.ts para el interceptor de refresco ────────────
// Se hace fuera del store para evitar importación circular entre api.ts y authStore.ts
registerAuthCallbacks({
  onTokenRefreshed: (accessToken) => {
    useAuthStore.getState().setAccessToken(accessToken)
  },

  onSessionExpired: () => {
    useAuthStore.getState().clearSession()
  },
})
